import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useDispatch } from 'react-redux';
import { useMeasure, useThrottleFn } from 'react-use';

import { createStyles, IconButton, Theme, withStyles, WithStyles, WithTheme } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { Send as SendIcon } from '@material-ui/icons';
import classnames from 'classnames';
import { camelize } from 'humps';
import { get, includes, isArray, isEmpty, isPlainObject, isString, map, trim } from 'lodash';
import * as Yup from 'yup';

import { useCurrentUser } from '@lib/hooks';
import { PatientFormTemplateItemTypeKeys } from '@lib/resources/types';
import { isAnyMobile } from '@lib/services/navigatorService';
import { capitalizeFirstLetter } from '@lib/services/stringsService';
import { getValidationSchema } from '@lib/services/validationsService';

import {
  MaskedInputDate,
  MaskedInputPhone,
  MaskedInputSSN,
  MaskedInputZipcode,
  TextField,
  TextFieldProps,
} from 'components/v2';
import { emitTyping } from 'containers/App/actions';
import { generateMessageTemplateKey } from 'hooks';

import { ChatContextProps, useChatContext } from '../../contexts/ChatContext';
import { TMessageWrapper } from '../Messages/MessageWrapper';

import TextFieldAddressAutocomplete, { TextFieldAddressAutocompleteValues } from './TextFieldAddressAutocomplete';
import TextFieldMedicationSearch, { TextFieldMedicationSearchValues } from './TextFieldMedicationSearch';
import TextFieldSelect, { TextFieldSelectValues } from './TextFieldSelect';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      alignItems: 'center',
      background: theme.palette.common.white,
      display: 'flex',
      flexDirection: 'column',
      padding: `0 ${theme.spacing.unit * 3}px ${theme.spacing.unit * 1.5}px ${theme.spacing.unit * 3}px`,
    },
    textFieldContainer: {
      width: '100%',
    },
    textField: {
      background: 'white',
      border: `1px solid ${fade(theme.palette.primary.dark, 0.2)}`,
      borderRadius: 10,
      boxSizing: 'border-box',
      justifyContent: 'center',
      minHeight: 48,
      width: '100%',

      '& textarea': {
        overflow: 'hidden',
        color: theme.custom.palette.colorSupportMidnight,
        minHeight: 20,

        '&::placeholder': {
          /* Chrome, Firefox, Opera, Safari 10.1+ */
          color: theme.custom.palette.colorSupportSteel,
          opacity: 1 /* Firefox */,
        },

        '&:-ms-input-placeholder': {
          /* Internet Explorer 10-11 */
          color: theme.custom.palette.colorSupportSteel,
        },

        '&::-ms-input-placeholder': {
          /* Microsoft Edge */
          color: theme.custom.palette.colorSupportSteel,
        },
      },
    },
    textFieldInput: {
      paddingLeft: theme.spacing.unit * 2,
      paddingRight: theme.spacing.unit / 2,
    },
    textFieldDisabled: {
      backgroundColor: theme.palette.grey[100],
    },
    textFieldAddressPopper: {
      width: '100%',
      margin: 20,
    },
    textFieldAddressPopperMobile: {
      marginLeft: 0,
    },
    hidden: {
      display: 'none',
    },
    sendIconButton: {
      color: theme.palette.primary.dark,
    },
    sendIcon: {
      height: '20px',
      width: '20px',
    },
  });

// TODO define placeholders per type
const messages = defineMessages({
  placeholder: {
    id: 'components.v2.TextFieldWrapper.placeholder',
    defaultMessage: 'Type here',
  },
});

const itemTypes: PatientFormTemplateItemTypeKeys[] = [
  // '2FA',
  'address',
  'date',
  'email',
  'long_text',
  'medication_search',
  'pharmacy_search',
  'phone_number',
  'short_text',
  'ssn',
  'zipcode',
  'dropdown',
];

const typeMap = {
  phone_number: 'tel',
  email: 'email',
};

const inputComponentMap = {
  date: MaskedInputDate,
  phone_number: MaskedInputPhone,
  ssn: MaskedInputSSN,
  zipcode: MaskedInputZipcode,
};

const isMobile = isAnyMobile();

export interface TextFieldWrapperProps
  extends WithStyles<typeof styles>,
    WithTheme,
    Omit<TextFieldProps, 'classes' | 'onSubmit' | 'required'> {
  message?: TMessageWrapper;
  onSubmit: ChatContextProps['pushAnswer'];
}

const getOutboundMessageText = (value: any) => {
  if (isArray(value)) return map(value, 'label').join(', ');
  if (isPlainObject(value)) return get(value, 'label');
  return value || '-';
};

const TextFieldWrapper: React.FC<TextFieldWrapperProps> = ({
  classes,
  className,
  theme,
  message,
  disabled,
  onSubmit,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const { isLiveChatEnabled, inputRef } = useChatContext();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const [state, setState] = useState<{ value: string | TextFieldMedicationSearchValues }>({
    value: '',
  });
  const [rootRef, { width }] = useMeasure();
  const metadata = message && message.metadata;
  const item = metadata && metadata.item;
  const type = get(item, 'type');
  const required = get(item, 'validations.required', false);
  const inputType = get(typeMap, type as keyof typeof typeMap, 'text');
  const inputComponent: any = type && inputComponentMap[type as keyof typeof inputComponentMap];
  const isSubmitDisabled =
    disabled ||
    (required && isString(state.value) && !trim(state.value)) ||
    (required && isArray(state.value) && isEmpty(state.value)) ||
    // Address and Dropdown requires the user to select a value from the popper
    (required && type === 'address') ||
    (required && type === 'dropdown');

  const isTypingDisabled =
    !isLiveChatEnabled && (disabled || (!includes(itemTypes, type) && !get(metadata, 'forceInput')));

  const handleSubmit = (
    value: string | TextFieldMedicationSearchValues | TextFieldSelectValues,
    answer?: TextFieldAddressAutocompleteValues | TextFieldSelectValues,
  ) => {
    let isValid = true;

    // Validate value
    if (isString(value) && (type === 'email' || type === 'phone_number' || type === 'date')) {
      // WORKAROUND schema expects `-` instead of `/`
      const valueSanitized = type === 'date' ? value.replace(/\//g, '-') : value;
      isValid = Yup.object({ ...getValidationSchema(type, type) }).isValidSync({ [type]: valueSanitized });
    }

    // Add an outbound message with the submitted value
    onSubmit(
      {
        type: 'outbound',
        text: getOutboundMessageText(value),
        metadata: { ...metadata, answer },
      },
      { skipNextItem: !isValid },
    );

    // Additional inbound message in case there's a validation error
    if (!isValid) {
      setTimeout(
        () => {
          // Use message template for validation error
          const text = generateMessageTemplateKey(
            `patientLumaBotValidationError${capitalizeFirstLetter(camelize(type!))}`,
          );
          onSubmit({ text, metadata: { item } }, { skipNextItem: true });
        }, // Delaying 1sec for better UX
        1000,
      );
    }

    // Reset values for the next item
    setTimeout(() => setState({ value: '' }));
  };

  // Emit the first typing event as soon as the patient updates the value
  // and use a 2 secs delay for the rest
  useThrottleFn(
    (value) => {
      if (value) dispatch(emitTyping({ type: 'patient', recipient: currentUser?.user }));
    },
    2000,
    [isLiveChatEnabled ? state.value : ''],
  );

  const textFieldProps = {
    type: inputType,
    placeholder: formatMessage(messages.placeholder),
    disabled: isTypingDisabled,
    onKeyDown:
      // Delegate submit on _Enter_ to the `TextFieldAddressAutocomplete` component
      // since it handles keyboard events to navigate and submit from its own menu
      type === 'address' || type === 'medication_search'
        ? undefined
        : (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' && !isMobile && !isSubmitDisabled) handleSubmit(state.value);
          },
    ...rest,
    InputProps: {
      className: type === 'medication_search' || type === 'dropdown' ? undefined : classes.textFieldInput,
      disableUnderline: true,
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            className={classes.sendIconButton}
            onClick={() => handleSubmit(state.value)}
            disabled={isSubmitDisabled || isTypingDisabled}
          >
            <SendIcon className={classes.sendIcon} />
          </IconButton>
        </InputAdornment>
      ),
      inputComponent,
      ...get(rest, 'InputProps'),
    },
  };

  // TODO: Move into `components/v2/TextFieldChat` so we can encapsulate all TextField related logic/styles
  return (
    <div ref={rootRef as React.LegacyRef<HTMLDivElement>} className={classnames(classes.root, className)}>
      <div className={classes.textFieldContainer}>
        <TextField
          inputRef={inputRef}
          className={classnames(classes.textField, {
            [classes.hidden]: type === 'medication_search' || type === 'address' || type === 'dropdown',
            [classes.textFieldDisabled]: textFieldProps.disabled,
          })}
          {...textFieldProps}
          required={false}
          multiline
          rowsMax={3}
          value={isArray(state.value) ? '' : state.value}
          onChange={({ target }) => {
            const value = target.value.replace(/\n/g, '');
            const newValue = type === 'email' ? value.replace(/ /g, '') : value;
            setState({ value: newValue });
          }}
        />
      </div>
      {/* Dropdown Input */}
      <TextFieldSelect
        className={classnames(classes.textField, { [classes.hidden]: type !== 'dropdown' })}
        value={state.value}
        shouldAutoFocus={type === 'dropdown'}
        onChange={(value: TextFieldSelectValues) => {
          setState({ value: '' });
          handleSubmit(value, value);
        }}
        options={get(item, 'properties.answers') || []}
        styles={{
          multiValue: (provided: any) => ({
            ...provided,
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.palette.primary.main}`,
          }),
          menu: (provided: any) => ({
            ...provided,
            marginLeft: theme.spacing.unit * -3,
            width: width + theme.spacing.unit * 5.5,
            boxShadow: 'none',
            borderTop: `1px solid ${theme.palette.divider}`,
            borderRadius: 0,
          }),
        }}
        {...textFieldProps}
      />
      {/* Medication Search Input */}
      <TextFieldMedicationSearch
        className={classnames(classes.textField, { [classes.hidden]: type !== 'medication_search' })}
        value={state.value}
        shouldAutoFocus={type === 'medication_search'}
        onChange={(values: TextFieldMedicationSearchValues) => {
          setState({ value: values === null ? '' : values });
        }}
        styles={{
          multiValue: (provided: any) => ({
            ...provided,
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.palette.primary.main}`,
          }),
          menu: (provided: any) => ({
            ...provided,
            marginLeft: theme.spacing.unit * -3,
            width: width + theme.spacing.unit * 5.5,
            boxShadow: 'none',
            borderTop: `1px solid ${theme.palette.divider}`,
            borderRadius: 0,
          }),
        }}
        {...textFieldProps}
      />
      {/*
      Using `react-places-autocomplete` for address values
      TODO: Check if we can use `react-select` with gplaces integration
      */}
      <TextFieldAddressAutocomplete
        className={classnames(classes.textField, { [classes.hidden]: type !== 'address' })}
        {...textFieldProps}
        value={isArray(state.value) ? '' : state.value}
        onChange={(value) => setState({ value })}
        onSelect={(place, values) => handleSubmit(values.value, values)}
        popperProps={{
          className: classnames(classes.textFieldAddressPopper, {
            [classes.textFieldAddressPopperMobile]: isMobile,
          }),
          placement: 'top',
          disablePortal: true,
        }}
      />
    </div>
  );
};

export default withStyles(styles, { name: 'TextFieldWrapper', withTheme: true })(TextFieldWrapper);
